import { skipToken } from '@reduxjs/toolkit/query/react';
import { useEffect, useMemo } from 'react';
import {
  SCHEDULE_VIEW_TAG,
  scheduleViewApi,
  useGetSchedulesReportsViewQuery,
  useGetScheduleViewKeyQuery,
} from '@/api/enhanced/schedule-view-api';
import { useAppDispatch } from '@/redux/hooks';
import type { IsoDateRange } from '@/utils/date-ranges';
import type { FilterSelection } from '@/utils/filter-selection';
import { toScheduleRows } from '@/utils/schedule-records';

export const SCHEDULE_PAGE_SIZE = 25;

interface UseScheduleDataArgs {
  selection: FilterSelection;
  dates: IsoDateRange;
  page: number;
}

const isNotFound = (error: unknown) =>
  typeof error === 'object' && error !== null && 'status' in error && error.status === 404;

// Nothing is requested until a form is selected: the report API needs at least one.
export const useScheduleData = ({ selection, dates, page }: UseScheduleDataArgs) => {
  const dispatch = useAppDispatch();
  const hasForms = selection.forms.length > 0;

  const view = useGetScheduleViewKeyQuery(
    hasForms
      ? {
          scheduleReportFiltersDto: {
            moduleFormIds: selection.forms,
            hierarchyFieldIds: selection.hierarchy,
            startDate: dates.from,
            endDate: dates.to,
          },
        }
      : skipToken
  );
  const hashKey = hasForms ? view.data?.hashKey : undefined;

  // Without includeDetails the server strips each record's status, and every past record would read as in progress.
  const report = useGetSchedulesReportsViewQuery(
    hashKey ? { viewId: hashKey, page, pageSize: SCHEDULE_PAGE_SIZE, includeDetails: true } : skipToken
  );
  const reportItems = hashKey ? report.data?.items : undefined;
  const isViewExpired = isNotFound(report.error);

  // Views expire server-side after a day; posting the same filters again brings the key back.
  const { refetch: refetchView } = view;
  useEffect(() => {
    if (isViewExpired) {
      void refetchView().then(() => dispatch(scheduleViewApi.util.invalidateTags([SCHEDULE_VIEW_TAG])));
    }
  }, [isViewExpired, refetchView, dispatch]);

  const rows = useMemo(() => toScheduleRows(reportItems ?? [], new Date()), [reportItems]);

  return {
    hasForms,
    hashKey,
    rows,
    availablePages: report.data?.availablePages ?? 0,
    totalSize: report.data?.totalSize ?? 0,
    isFetching: view.isFetching || report.isFetching,
    isError: view.isError || (report.isError && !(isViewExpired && view.isFetching)),
  };
};
